import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft } from "react-icons/fa";
import { API_URL } from "../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#4F46E5" },
    error: { main: "#EF4444" },
    background: { default: "#F5F7FF", paper: "#FFFFFF" },
    text: { primary: "#1E1B4B", secondary: "#6B7280" },
  },
  typography: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif' },
  shape: { borderRadius: 16 },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: "#F9FAFB",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#4F46E5",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#4F46E5",
            borderWidth: 2,
          },
        },
        notchedOutline: { borderColor: "#E5E7EB" },
      },
    },
  },
});

/* ── animation variants ── */
const pageVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20, height: 0 },
  visible: {
    opacity: 1,
    x: 0,
    height: "auto",
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    x: 20,
    height: 0,
    transition: { duration: 0.25, ease: "easeIn" },
  },
};

const AdminLocationPage = () => {
  const { countryId } = useParams();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [state, setState] = useState("");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setRefreshing(true);
      try {
        const response = await axios.get(
          `${API_URL}/admin/locations/${countryId}/`
        );
        setLocations(response.data);
      } catch (error) {
        console.error("Error fetching locations:", error);
      } finally {
        setRefreshing(false);
      }
    };
    fetchData();
  }, [countryId]);

  const createLocation = async () => {
    if (!state.trim()) return;
    setAdding(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/locations/${countryId}/`,
        { state }
      );
      setLocations([...locations, response.data]);
      setState("");
    } catch (error) {
      console.error("Error creating location:", error);
    } finally {
      setAdding(false);
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Are you sure you want to delete this location?"))
      return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/admin/location/${id}/`);
      setLocations(locations.filter((l) => l.id !== id));
    } catch (error) {
      console.error("Error deleting location:", error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") createLocation();
  };

  const filtered = locations.filter((l) =>
    l.state.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: "100%",
         
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #F5F7FF 0%, #EEF2FF 50%, #FFF7ED 100%)",
          p: { xs: 2, sm: 4 },
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1152px", // Exact max-w-6xl
            mx: "auto", // mx-auto
          }}
        >
          <motion.div
            variants={pageVariants}
            initial="hidden"
            animate="visible"
          >
            {/* ── header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
              <motion.div whileHover={{ x: -4 }} whileTap={{ scale: 0.92 }}>
                <Button
                  startIcon={<FaArrowLeft size={13} />}
                  onClick={() => navigate(-1)}
                  sx={{
                    color: "#4F46E5",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    borderRadius: 3,
                    px: 2,
                    py: 1,
                    background: "rgba(79,70,229,0.08)",
                    "&:hover": { background: "rgba(79,70,229,0.15)" },
                  }}
                >
                  Back
                </Button>
              </motion.div>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="#1E1B4B"
                  letterSpacing="-0.5px"
                >
                  Locations
                </Typography>
                <Typography fontSize="0.78rem" color="#6B7280">
                  Manage states for this country
                </Typography>
              </Box>
            </Box>

            {/* ── stats banner ── */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  color: "#fff",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.07)",
                  }}
                />
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FmdGoodOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography fontWeight={800} fontSize="1.5rem" lineHeight={1}>
                    {locations.length}
                  </Typography>
                  <Typography fontSize="0.8rem" sx={{ opacity: 0.85 }}>
                    Total States Registered
                  </Typography>
                </Box>
              </Paper>
            </motion.div>

            {/* ── add input ── */}
            <motion.div
              animate={{
                boxShadow: inputFocused
                  ? "0 0 0 4px rgba(79,70,229,0.12)"
                  : "0 0 0 0px transparent",
              }}
              style={{ borderRadius: 16 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 4,
                  border: inputFocused
                    ? "1.5px solid #4F46E5"
                    : "1.5px solid #E0E7FF",
                  background: "#fff",
                  transition: "border-color 0.2s",
                }}
              >
                <Typography
                  fontWeight={700}
                  fontSize="0.8rem"
                  color="#6B7280"
                  mb={1.5}
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                >
                  Add New State
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter state name…"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon
                            sx={{ color: "#4F46E5", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "0.9rem",
                        fontFamily: '"DM Sans", sans-serif',
                      },
                    }}
                  />
                  <motion.div
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <Button
                      variant="contained"
                      onClick={createLocation}
                      disabled={!state.trim() || adding}
                      startIcon={
                        adding ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <AddRoundedIcon />
                        )
                      }
                      sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 700,
                        px: 2.5,
                        whiteSpace: "nowrap",
                        background:
                          "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)",
                        boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
                        "&:hover": {
                          boxShadow: "0 6px 20px rgba(79,70,229,0.45)",
                        },
                        "&:disabled": {
                          background: "#E0E7FF",
                          boxShadow: "none",
                          color: "#A5B4FC",
                        },
                      }}
                    >
                      {adding ? "Adding…" : "Add"}
                    </Button>
                  </motion.div>
                </Box>
              </Paper>
            </motion.div>

            {/* ── search ── */}
            {locations.length > 3 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search states…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon
                          sx={{ color: "#9CA3AF", fontSize: 18 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-input": {
                      fontSize: "0.875rem",
                      fontFamily: '"DM Sans", sans-serif',
                    },
                  }}
                />
              </motion.div>
            )}

            {/* ── list ── */}
            <AnimatePresence mode="wait">
              {refreshing ? (
                <motion.div
                  key="loader"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      py: 6,
                    }}
                  >
                    <CircularProgress
                      sx={{ color: "#4F46E5" }}
                      size={36}
                      thickness={4}
                    />
                    <Typography color="#6B7280" fontSize="0.85rem">
                      Loading locations…
                    </Typography>
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  variants={listVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: "1.5px solid #E0E7FF",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {filtered.length === 0 ? (
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <Typography color="#6B7280" fontSize="0.9rem">
                          {search
                            ? "No matching states found."
                            : "No locations added yet."}
                        </Typography>
                      </Box>
                    ) : (
                      <AnimatePresence>
                        {filtered.map((item, index) => (
                          <motion.div
                            key={item.id}
                            variants={itemVariants}
                            exit="exit"
                            layout
                          >
                            <Box
                              sx={{
                                px: 3,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                "&:hover": { background: "#F5F7FF" },
                                transition: "background 0.2s",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <motion.div
                                  whileHover={{ rotate: 15, scale: 1.1 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: 2,
                                      background: "#EEF2FF",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <LocationOnIcon
                                      sx={{ color: "#4F46E5", fontSize: 16 }}
                                    />
                                  </Box>
                                </motion.div>
                                <Typography
                                  fontWeight={600}
                                  fontSize="0.9rem"
                                  color="#1E1B4B"
                                >
                                  {item.state}
                                </Typography>
                              </Box>

                              <Tooltip title="Delete location" placement="left">
                                <motion.div
                                  whileHover={{ scale: 1.12 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => deleteLocation(item.id)}
                                    disabled={deletingId === item.id}
                                    sx={{
                                      color: "#EF4444",
                                      background: "#FEF2F2",
                                      borderRadius: 2,
                                      width: 34,
                                      height: 34,
                                      "&:hover": { background: "#FEE2E2" },
                                      "&:disabled": {
                                        color: "#FCA5A5",
                                        background: "#FEF2F2",
                                      },
                                    }}
                                  >
                                    {deletingId === item.id ? (
                                      <CircularProgress
                                        size={14}
                                        sx={{ color: "#EF4444" }}
                                      />
                                    ) : (
                                      <DeleteOutlineRoundedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    )}
                                  </IconButton>
                                </motion.div>
                              </Tooltip>
                            </Box>
                            {index < filtered.length - 1 && (
                              <Divider sx={{ mx: 3, borderColor: "#F3F4F6" }} />
                            )}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    )}
                  </Paper>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AdminLocationPage;
